import { ButtonPrimary } from '@/components/button'
import { ArrowBack, ChevronRight } from '@/components/icons'
import {
  AutocompleteAddress,
  AutocompleteLocationInput,
  BirthdayDateInput,
  GenderSwitch,
  PhoneInput,
  TextInput,
} from '@/components/input'
import { eventTracker } from '@/config/trackingFunctions'
import { duprCountries } from '@/constants/Countries'
import { ROUTE_EXTERNAL_CREATE_PASSWORD } from '@/constants/Routes'
import { saveAddress, useClaimDetail } from '@/services'
import { Country, Gender } from '@/types'
import ExternalSignUpValidationSchema from '@/utils/schema/ExternalSignUpValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ExternalScreen from './ExternalScreen'
import { AccountSearchInput } from './components'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'

type FormValues = {
  fullName: string
  birthDate: string
  email: string
  phone: {
    phoneNumber: string
    isoCode: string
    dialCode: string
  }
  confirmEmail: string
  gender: Gender
  location: AutocompleteAddress
  termsOfService: boolean
}

export const ExternalSignUp = () => {
  const { t } = useTranslation()

  const { partnerId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  // No error banner on this step any more: its only writer was the /auth/email/v1.0/exists
  // pre-check. A taken email is now reported by the signup call on the create-password step,
  // which has its own error surface.
  const [selectedCountry, setSelectedCountry] = useState<Country>(duprCountries[0])

  const queryParameters = new URLSearchParams(window.location.search)
  const uniqueIdentifier = queryParameters.get('externalIdentifier')
  const defaultFullName = queryParameters.get('fullName') || location.state?.claimDetail?.fullname || ''
  const defaultEmail = queryParameters.get('email') || ''
  const defaultBirthDate = queryParameters.get('birthDate') || ''

  const { data: playerData } = useClaimDetail(location.state?.playerId)

  const goBack = () => {
    navigate(-1)
  }

  const defaultValues = {
    fullName: defaultFullName,
    birthDate: defaultBirthDate,
    phone: {
      phoneNumber: '',
      isoCode: selectedCountry.isoCode,
      dialCode: selectedCountry.dialCode,
    },
    gender: Gender.MALE,
    email: defaultEmail,
    confirmEmail: '',
    location: {
      description: '',
      placeId: '',
    },
    termsOfService: false,
  }

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    register,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(ExternalSignUpValidationSchema as any),
    mode: 'onSubmit',
  })

  const onChangePhoneNumber = (phoneNumber: string) => {
    const regex = /^[0-9]*$/
    if (regex.test(phoneNumber)) {
      setValue('phone.phoneNumber', phoneNumber, { shouldDirty: true, shouldValidate: true })
    }
  }

  useEffect(() => {
    reset(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = handleSubmit(async (data) => {
    let addressId: any
    if (data.location && data.location.placeId) {
      const result = await saveAddress({ placeId: data.location.placeId })
      addressId = result.result.id
    }
    // Duplicate-email detection happens on the signup call itself (the create-password step).
    // The old pre-flight check against /auth/email/v1.0/exists was an account-enumeration oracle
    // and that endpoint no longer exists.
    const accData = {
      addressId: addressId,
      birthdate: moment(data.birthDate).format('YYYY-MM-DD'),
      email: data.email,
      fullName: data.fullName?.trim() || '',
      gender: data.gender,
      identifier: uniqueIdentifier ? uniqueIdentifier : null,
      termsOfService: data.termsOfService,
      phone: data.phone.phoneNumber,
      isoCode: selectedCountry.isoCode,
      dialCode: selectedCountry.dialCode,
      ...(playerData?.id && {
        userId: playerData.id,
      }),
    }

    eventTracker(TRACKING_TYPES.PARTNER_SIGNUP_SUCCESS, {
      'Page Name': PAGE_NAMES.PARTNER_SIGNUP,
      partner_id: partnerId,
    })

    navigate(`${ROUTE_EXTERNAL_CREATE_PASSWORD}/${partnerId}`, {
      state: {
        accData: accData,
      },
    })
  })

  const handleBirthdayDateInputChange = (date: Date | null) => {
    if (date) {
      setValue('birthDate', moment(date).format('YYYY-MM-DD'), {
        shouldDirty: true,
      })
    }
  }

  return (
    <ExternalScreen>
      <div className="w-full h-full flex flex-col gap-y-6 items-center">
        <div className="w-full h-full ">
          <div className="flex flex-row items-center justify-between w-full text-navy-900 mt-6 px-4">
            <ArrowBack width={48} height={48} onClick={goBack} className="cursor-pointer" />
            <div onClick={onSubmit} className="cursor-pointer flex flex-row items-center opacity-50">
              <p className="text-sm font-semibold">Next</p>
              <ChevronRight />
            </div>
          </div>

          <div className="px-4">
            <p className="text-[28px] font-bold text-navy-900 p-4">Create Account</p>
          </div>
        </div>
        <form className="w-full h-[570px] px-4 pb-5" onSubmit={onSubmit}>
          <div className="w-full h-full overflow-auto flex flex-col bg-white px-6 gap-y-6">
            <AccountSearchInput
              label="Full Name"
              labelClassName="mb-2 text-sm font-semibold text-navy-900"
              containerClassName={`border-[1px]`}
              className={`text-navy-900 text-sm font-medium`}
              {...register('fullName')}
              placeholder="Enter Your Name"
              error={errors.fullName?.message}
            />
            <BirthdayDateInput
              label={t('common.birthDate')}
              placeholderText={t('common.birthDatePlaceHolder')}
              containerClassName="w-full"
              labelClassName="!text-navy-900"
              selected={watch('birthDate')}
              onChange={(date: Date | null) => handleBirthdayDateInputChange(date)}
              error={errors.birthDate?.message}
            />
            <PhoneInput
              label="Phone (Optional)"
              inputClassName={`border-[1px]`}
              placeholder="Enter Phone Number"
              selectCountryButtonClassName="px-3 py-2"
              selectCountryListBoxClassName="min-w-[90px]"
              labelClassName="!text-navy-900"
              optionsClassName="!top-full"
              selectedCountry={selectedCountry}
              phoneNumber={watch('phone.phoneNumber')}
              setPhoneNumber={(phoneNumber) => onChangePhoneNumber(phoneNumber)}
              setSelectedCountry={setSelectedCountry}
              error={errors.phone?.phoneNumber?.message}
              maxLength={15}
            />
            <TextInput
              placeholder="Enter Email"
              labelClassName="!text-navy-900"
              label="Email"
              error={errors?.email?.message}
              {...register('email')}
            />
            <TextInput
              placeholder="Enter Confirm Email"
              labelClassName="!text-navy-900"
              label="Confirm Email"
              onPaste={(e) => e.preventDefault()}
              error={errors?.confirmEmail?.message}
              {...register('confirmEmail')}
            />
            <GenderSwitch
              label="Gender"
              labelClassName="!text-navy-900"
              buttonClassName="!text-navy-900"
              selectedButtonClassName="!text-white"
              value={watch('gender')}
              setValue={(gender) => setValue('gender', gender, { shouldDirty: true, shouldValidate: true })}
            />
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <AutocompleteLocationInput
                  {...field}
                  regionsOnly
                  label="Location"
                  containerClassName="!flex-1"
                  labelClassName="!text-navy-900"
                  setValue={(location) =>
                    setValue('location', location, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  iconClassName="!text-navy-900"
                  error={errors.location?.description?.message}
                />
              )}
            />

            <div className="flex items-start gap-x-2">
              <input
                id="termsOfService"
                type="checkbox"
                className="h-5 w-5 flex-shrink-0 cursor-pointer rounded border-navy-900/20 text-primary-500 focus:ring-primary-500"
                {...register('termsOfService')}
                aria-describedby="terms-error"
              />
              <div className="text-sm">
                <label htmlFor="termsOfService" className="text-navy-900">
                  {t('auth.iAgreeToThe')}{' '}
                  <a
                    href="https://www.dupr.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    {t('auth.termsOfService')}
                  </a>{' '}
                  {t('common.and')}{' '}
                  <a
                    href="https://www.dupr.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    {t('auth.privacyPolicy')}
                  </a>
                </label>
                {errors.termsOfService && (
                  <p id="terms-error" className="mt-1 text-sm text-red-500">
                    {errors.termsOfService.message}
                  </p>
                )}
              </div>
            </div>

            <div className="w-full text-center pb-5">
              <ButtonPrimary
                type="submit"
                title="Continue"
                className="w-[240px] px-[14px] py-5"
                disabled={
                  !watch('fullName') ||
                  !watch('birthDate') ||
                  !watch('email') ||
                  !watch('confirmEmail') ||
                  !watch('location.placeId') ||
                  !watch('termsOfService')
                }
                onClick={onSubmit}
              />
            </div>
          </div>
        </form>
      </div>
    </ExternalScreen>
  )
}

export default ExternalSignUp
